import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiMagnifyingGlass, HiXMark } from 'react-icons/hi2';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../../services/api';

const EMPTY_FORM = {
  name: '', phone: '', email: '', address: '', area: '', city: '', pincode: '',
  deposit_paid: '', notes: '', create_login: false, login_email: '', login_password: '',
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCustomers = useCallback(() => {
    setLoading(true);
    const params = { page, limit, search: search || undefined, area: areaFilter || undefined };
    getCustomers(params)
      .then((res) => {
        const data = res.data;
        setCustomers(Array.isArray(data) ? data : data.customers || []);
        setTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => toast.error('Failed to load customers.'))
      .finally(() => setLoading(false));
  }, [page, search, areaFilter]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const openAdd = () => { setEditCustomer(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (c) => {
    setEditCustomer(c);
    setForm({
      name: c.name || '', phone: c.phone || '', email: c.email || '',
      address: c.address || '', area: c.area || '', city: c.city || '',
      pincode: c.pincode || '', deposit_paid: c.deposit_paid ?? '',
      notes: c.notes || '', create_login: false, login_email: '', login_password: '',
    });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditCustomer(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error('Name, Phone, and Address are required.');
      return;
    }
    if (form.create_login && (!form.login_email.trim() || !form.login_password.trim())) {
      toast.error('Email and Password required for login account.');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, phone: form.phone, email: form.email,
      address: form.address, area: form.area, city: form.city, pincode: form.pincode,
      deposit_paid: form.deposit_paid ? Number(form.deposit_paid) : 0,
      notes: form.notes,
      ...(form.create_login && !editCustomer ? {
        create_login: true, login_email: form.login_email, login_password: form.login_password,
      } : {}),
    };
    try {
      if (editCustomer) {
        await updateCustomer(editCustomer.id || editCustomer._id, payload);
        toast.success('Customer updated.');
      } else {
        await createCustomer(payload);
        toast.success('Customer added.');
      }
      closeModal();
      fetchCustomers();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCustomer(deleteTarget.id || deleteTarget._id);
      toast.success('Customer deleted.');
      setDeleteTarget(null);
      fetchCustomers();
    } catch {
      toast.error('Failed to delete customer.');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Customers</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <HiPlus /> Add Customer
        </button>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <HiMagnifyingGlass style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            className="form-control"
            style={{ paddingLeft: '2rem' }}
            placeholder="Search name, phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <input
          className="form-control"
          style={{ width: '160px' }}
          placeholder="Filter by area"
          value={areaFilter}
          onChange={(e) => { setAreaFilter(e.target.value); setPage(1); }}
        />
        {(search || areaFilter) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setSearch(''); setAreaFilter(''); setPage(1); }}>
            <HiXMark /> Clear
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Area</th>
                  <th>Balance Due</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8' }}>No customers found.</td></tr>
                ) : (
                  customers.map((c) => (
                    <tr key={c.id || c._id}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td>{c.phone}</td>
                      <td>{c.email || '-'}</td>
                      <td>{c.area || '-'}</td>
                      <td>Rs.{new Intl.NumberFormat('en-IN').format(Number(c.balance_due || c.balance || 0))}</td>
                      <td>
                        <span className={`badge ${c.status === 'active' || c.is_active ? 'badge-green' : 'badge-gray'}`}>
                          {c.status || (c.is_active ? 'active' : 'inactive')}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>
                            <HiPencil /> Edit
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(c)}>
                            <HiTrash /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > limit && (
          <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)' }}>
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: '620px' }}>
            <div className="modal-header">
              <span className="modal-title">{editCustomer ? 'Edit Customer' : 'Add Customer'}</span>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}><HiXMark /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-control" name="name" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input className="form-control" name="phone" value={form.phone} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" name="email" value={form.email} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area</label>
                    <input className="form-control" name="area" value={form.area} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <textarea className="form-control" name="address" value={form.address} onChange={handleChange} rows={2} required />
                </div>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-control" name="city" value={form.city} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <input className="form-control" name="pincode" value={form.pincode} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Deposit Paid (Rs.)</label>
                    <input className="form-control" type="number" name="deposit_paid" value={form.deposit_paid} onChange={handleChange} min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" name="notes" value={form.notes} onChange={handleChange} rows={2} />
                </div>

                {!editCustomer && (
                  <>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" id="create_login" name="create_login" checked={form.create_login} onChange={handleChange} />
                      <label htmlFor="create_login" style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                        Create Login Account
                      </label>
                    </div>
                    {form.create_login && (
                      <div className="grid grid-2">
                        <div className="form-group">
                          <label className="form-label">Login Email *</label>
                          <input className="form-control" type="email" name="login_email" value={form.login_email} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Password *</label>
                          <input className="form-control" type="password" name="login_password" value={form.login_password} onChange={handleChange} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editCustomer ? 'Update' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <span className="modal-title">Delete Customer</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteTarget(null)}><HiXMark /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
